import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import type { MeasurementBracketData } from "./types";

const { fontFamily } = loadFont("normal", { weights: ["600", "700"], subsets: ["latin"] });

const GOLD = "#D4A853";
const W = 1280, H = 704;
const CAP = 30;
const STROKE = 3;

export const MeasurementBracket: React.FC<{ data: MeasurementBracketData; durationInFrames: number }> = ({ data, durationInFrames }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const drawProgress = interpolate(frame, [0, Math.round(1.0 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.25, 0.46, 0.45, 0.94),
  });
  const labelOpacity = interpolate(frame, [Math.round(0.85 * fps), Math.round(1.25 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const capProgress = interpolate(drawProgress, [0.82, 1.0], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.5 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const fx = data.from.x * W;
  const fy = data.from.y * H;
  const tx = data.to.x * W;
  const ty = data.to.y * H;

  if (data.axis === "vertical") {
    const currentTy = fy + (ty - fy) * drawProgress;
    const midY = (fy + ty) / 2;
    // Place label box to the right of the bracket line
    const labelX = fx + 20;
    const boxW = 190, boxH = 52;

    return (
      <AbsoluteFill style={{ opacity: fadeOut, pointerEvents: "none" }}>
        <svg width={W} height={H} style={{ position: "absolute", top: 0, left: 0 }}>
          {/* Main vertical line */}
          <line x1={fx} y1={fy} x2={fx} y2={currentTy} stroke={GOLD} strokeWidth={STROKE} />
          {/* Bottom end cap (always shown from start) */}
          <line x1={fx - CAP} y1={fy} x2={fx + CAP} y2={fy} stroke={GOLD} strokeWidth={STROKE} />
          {/* Top end cap (fades in at end of draw) */}
          <line x1={tx - CAP} y1={ty} x2={tx + CAP} y2={ty} stroke={GOLD} strokeWidth={STROKE} opacity={capProgress} />

          {/* Label box */}
          <g opacity={labelOpacity}>
            <rect
              x={labelX} y={midY - boxH / 2}
              width={boxW} height={boxH}
              rx={8}
              fill="rgba(0,0,0,0.82)"
              stroke={GOLD} strokeOpacity={0.4} strokeWidth={1}
            />
            <text
              x={labelX + boxW / 2} y={midY - 7}
              textAnchor="middle" dominantBaseline="middle"
              fill={GOLD} fontSize={20} fontWeight={700} fontFamily={fontFamily}
            >
              {data.label}
            </text>
            {data.label_secondary && (
              <text
                x={labelX + boxW / 2} y={midY + 14}
                textAnchor="middle" dominantBaseline="middle"
                fill="rgba(255,255,255,0.6)" fontSize={12} fontWeight={600} fontFamily={fontFamily}
              >
                {data.label_secondary}
              </text>
            )}
          </g>
        </svg>
      </AbsoluteFill>
    );
  }

  // Horizontal bracket (draw_across)
  const currentTx = fx + (tx - fx) * drawProgress;
  const midX = (fx + tx) / 2;
  const labelY = fy - 66;
  const boxW = 200, boxH = 52;

  return (
    <AbsoluteFill style={{ opacity: fadeOut, pointerEvents: "none" }}>
      <svg width={W} height={H} style={{ position: "absolute", top: 0, left: 0 }}>
        {/* Main horizontal line */}
        <line x1={fx} y1={fy} x2={currentTx} y2={fy} stroke={GOLD} strokeWidth={STROKE} />
        {/* Left end cap */}
        <line x1={fx} y1={fy - CAP} x2={fx} y2={fy + CAP} stroke={GOLD} strokeWidth={STROKE} />
        {/* Right end cap */}
        <line x1={tx} y1={ty - CAP} x2={tx} y2={ty + CAP} stroke={GOLD} strokeWidth={STROKE} opacity={capProgress} />

        {/* Label box above midpoint */}
        <g opacity={labelOpacity}>
          <rect
            x={midX - boxW / 2} y={labelY}
            width={boxW} height={boxH}
            rx={8}
            fill="rgba(0,0,0,0.82)"
            stroke={GOLD} strokeOpacity={0.4} strokeWidth={1}
          />
          <text
            x={midX} y={labelY + 18}
            textAnchor="middle" dominantBaseline="middle"
            fill={GOLD} fontSize={20} fontWeight={700} fontFamily={fontFamily}
          >
            {data.label}
          </text>
          {data.label_secondary && (
            <text
              x={midX} y={labelY + 38}
              textAnchor="middle" dominantBaseline="middle"
              fill="rgba(255,255,255,0.6)" fontSize={12} fontWeight={600} fontFamily={fontFamily}
            >
              {data.label_secondary}
            </text>
          )}
          {/* Connector line from box to bracket */}
          <line
            x1={midX} y1={labelY + boxH}
            x2={midX} y2={fy - 4}
            stroke={GOLD} strokeWidth={1} strokeOpacity={0.4} strokeDasharray="3 3"
          />
        </g>
      </svg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
